import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Factory } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { adminApi, formatApiErrorDetail } from "../lib/authApi";

const STATUS_STYLES = {
  pending: "bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/30",
  approved: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  rejected: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30",
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

export const AdminPartnersSection = () => {
  const [partners, setPartners] = useState(null);
  const [acting, setActing] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/partners").then((r) => setPartners(r.data)).catch(() => {
      toast.error("Failed to load partners.");
      setPartners([]);
    });
  }, []);

  const setStatus = async (partner, status) => {
    let note = "";
    if (status === "rejected") {
      note = window.prompt("Rejection note for the partner (optional):") || "";
    }
    setActing(partner.id);
    try {
      await adminApi.patch(`/admin/partners/${partner.id}/status`, { status, note });
      setPartners(partners.map((p) => (p.id === partner.id ? { ...p, partner_status: status, review_note: note } : p)));
      toast.success(status === "approved" ? "Partner approved — notification email queued." : "Partner rejected.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActing(null);
    }
  };

  if (!partners) {
    return <div className="py-12 flex justify-center" data-testid="admin-partners-loading"><Loader2 className="h-5 w-5 animate-spin text-primary" strokeWidth={1.5} /></div>;
  }

  if (partners.length === 0) {
    return <p className="text-sm text-muted-foreground py-8" data-testid="admin-partners-empty">No partner registrations yet.</p>;
  }

  return (
    <div className="space-y-4" data-testid="admin-partners-section">
      {partners.map((p) => (
        <div key={p.id} className="p-6 rounded-xl bg-card border border-border" data-testid={`admin-partner-card-${p.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Factory className="h-5 w-5 text-primary" strokeWidth={1.5} />
              <h4 className="font-semibold font-heading">{p.company_name || "—"}</h4>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-semibold uppercase ${STATUS_STYLES[p.partner_status] || STATUS_STYLES.pending}`} data-testid={`admin-partner-status-${p.id}`}>
                {p.partner_status}
              </span>
            </div>
            <span className="text-xs text-muted-foreground">Applied {fmtDate(p.created_at)}</span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {p.name} · {p.email}{p.phone ? ` · ${p.phone}` : ""}{(p.city || p.state) ? ` · ${[p.city, p.state].filter(Boolean).join(", ")}` : ""}
          </p>
          <div className="mt-3 grid sm:grid-cols-3 gap-x-8 gap-y-2 text-sm">
            <span className="text-muted-foreground">GST: <b className="text-foreground">{p.gst_number || "—"}</b></span>
            <span className="text-muted-foreground">Factory: <b className="text-foreground">{p.factory_size || "—"}</b></span>
            <span className="text-muted-foreground">Years: <b className="text-foreground">{p.years_in_business ?? "—"}</b></span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {(p.processes || []).map((pr) => <Badge key={pr} variant="secondary">{pr}</Badge>)}
            {(p.certifications || []).map((c) => <Badge key={c} variant="outline">{c}</Badge>)}
          </div>
          {(p.machines || []).length > 0 && (
            <div className="mt-3 space-y-1.5">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-mono-tech">Machines ({p.machines.length})</p>
              {p.machines.map((m) => (
                <div key={m.id} className="text-sm text-muted-foreground">
                  • {m.name}{m.machine_type ? ` (${m.machine_type})` : ""}{m.process ? ` — ${m.process}` : ""} × {m.quantity}
                </div>
              ))}
            </div>
          )}
          {p.review_note && <p className="mt-3 text-xs text-muted-foreground">Review note: {p.review_note}</p>}
          {p.reviewed_at && <p className="mt-1 text-xs text-muted-foreground">Reviewed {fmtDate(p.reviewed_at)} by {p.reviewed_by}</p>}
          <div className="mt-4 flex gap-3">
            {p.partner_status !== "approved" && (
              <Button size="sm" disabled={acting === p.id} onClick={() => setStatus(p, "approved")} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`admin-partner-approve-btn-${p.id}`}>
                {acting === p.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" strokeWidth={1.5} /> : <CheckCircle2 className="h-4 w-4 mr-1.5" strokeWidth={1.5} />}
                Approve
              </Button>
            )}
            {p.partner_status !== "rejected" && (
              <Button size="sm" variant="outline" disabled={acting === p.id} onClick={() => setStatus(p, "rejected")} className="rounded-full text-destructive border-destructive/40 hover:bg-destructive/10" data-testid={`admin-partner-reject-btn-${p.id}`}>
                <XCircle className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Reject
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
